import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle, Clock, Shield } from 'lucide-react';
import apiService from '../../services/api';
import { VendorComplianceAttention } from '../../types';
import { getComplianceStatusClasses } from '../../utils/vendorCompliance';
import DataGrid from '../grid/DataGrid';

const getAttentionStatusLabel = (vendor: VendorComplianceAttention) => {
  if (vendor.hasPendingReview && vendor.complianceStatusLabel === 'Compliant') {
    return 'Needs Review';
  }

  return vendor.complianceStatusLabel;
};

const CompanyComplianceSummaryPanel: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState(() => {
    const page = searchParams.get('page');
    return page ? parseInt(page, 10) : 1;
  });
  const [pageSize, setPageSize] = useState(() => {
    const size = searchParams.get('pageSize');
    return size ? parseInt(size, 10) : 10;
  });

  const { data: summaryResponse, isLoading: isSummaryLoading } = useQuery({
    queryKey: ['company-compliance-summary'],
    queryFn: () => apiService.getCompanyComplianceSummary(),
  });

  const summary = summaryResponse?.data;
  const needsAttentionCount = summary?.needsAttentionCount ?? 0;

  const {
    data: needsAttentionResponse,
    isLoading: isNeedsAttentionLoading,
  } = useQuery({
    queryKey: ['company-compliance-needs-attention', currentPage, pageSize],
    queryFn: () => apiService.getCompanyComplianceNeedsAttention(currentPage, pageSize),
    enabled: needsAttentionCount > 0,
  });

  const needsAttention = needsAttentionResponse?.data?.data ?? [];
  const totalCount = needsAttentionResponse?.data?.totalCount ?? 0;
  const totalPages = needsAttentionResponse?.data?.totalPages ?? 1;

  const buildReturnUrl = () => {
    const params = new URLSearchParams();
    if (currentPage > 1) params.set('page', currentPage.toString());
    if (pageSize !== 10) params.set('pageSize', pageSize.toString());
    const query = params.toString();
    return `/admin/vendor-compliance${query ? `?${query}` : ''}`;
  };

  const columns = useMemo(
    () => [
      {
        id: 'vendorName',
        header: 'Vendor',
        sortable: false,
        cell: (_value: string, row: VendorComplianceAttention) => (
          <div className="font-medium text-gray-900 dark:text-gray-100">{row.vendorName}</div>
        ),
      },
      {
        id: 'primaryIssue',
        header: 'Primary Issue',
        sortable: false,
        cell: (_value: string | undefined, row: VendorComplianceAttention) => (
          <div className="text-sm text-gray-600 dark:text-gray-400">{row.primaryIssue || '—'}</div>
        ),
      },
      {
        id: 'complianceStatusLabel',
        header: 'Status',
        sortable: false,
        cell: (_value: string, row: VendorComplianceAttention) => (
          <span
            className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold ${getComplianceStatusClasses(
              row.complianceStatus,
              row.complianceStatusLabel
            )}`}
          >
            {getAttentionStatusLabel(row)}
          </span>
        ),
      },
    ],
    []
  );

  if (isSummaryLoading) {
    return (
      <div className="card">
        <div className="card-body text-sm text-gray-500">Loading compliance overview...</div>
      </div>
    );
  }

  if (!summary) {
    return null;
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <SummaryCard label="Total Vendors" value={summary.totalVendors} icon={<Shield className="h-4 w-4" />} />
        <SummaryCard label="Compliant" value={summary.compliantCount} tone="green" icon={<CheckCircle className="h-4 w-4" />} />
        <SummaryCard label="Expiring Soon" value={summary.expiringSoonCount} tone="yellow" icon={<Clock className="h-4 w-4" />} />
        <SummaryCard label="Non-Compliant" value={summary.nonCompliantCount} tone="red" icon={<AlertTriangle className="h-4 w-4" />} />
        <SummaryCard label="Needs Review" value={summary.pendingReviewCount} tone="amber" icon={<Clock className="h-4 w-4" />} />
      </div>

      {needsAttentionCount > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Vendors Needing Attention
            </h2>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {needsAttentionCount} vendor{needsAttentionCount === 1 ? '' : 's'}
            </span>
          </div>

          <DataGrid
            data={needsAttention}
            columns={columns}
            isLoading={isNeedsAttentionLoading}
            currentPage={currentPage}
            pageSize={pageSize}
            totalCount={totalCount}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setCurrentPage(1);
            }}
            emptyMessage="No vendors needing attention"
            onRowClick={(row) => {
              const vendor = row as VendorComplianceAttention;
              const returnUrl = encodeURIComponent(buildReturnUrl());
              navigate(`/vendors/${vendor.vendorId}/edit?tab=compliance&returnUrl=${returnUrl}`);
            }}
            rowTooltip="Open vendor compliance"
            maxHeight="480px"
          />
        </div>
      )}
    </div>
  );
};

const SummaryCard: React.FC<{
  label: string;
  value: number;
  icon: React.ReactNode;
  tone?: 'green' | 'yellow' | 'red' | 'amber';
}> = ({ label, value, icon, tone }) => {
  const toneClass =
    tone === 'green'
      ? 'text-green-700 dark:text-green-300'
      : tone === 'yellow'
        ? 'text-yellow-700 dark:text-yellow-300'
        : tone === 'red'
          ? 'text-red-700 dark:text-red-300'
          : tone === 'amber'
            ? 'text-amber-700 dark:text-amber-300'
            : 'text-gray-900 dark:text-gray-100';

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3">
      <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-1">
        {icon}
        <span>{label}</span>
      </div>
      <div className={`text-2xl font-semibold ${toneClass}`}>{value}</div>
    </div>
  );
};

export default CompanyComplianceSummaryPanel;
